import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import {
  catchError,
  map,
  of,
  tap,
  throwError,
  type Observable,
  filter,
  switchMap,
  shareReplay,
  take,
} from 'rxjs';
import { Logger } from '@shared/helpers/logger';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@environments/environment';
import { AUTH_ENDPOINTS, GLOBAL_CONSTANTS, ROUTE_PATH } from '../models/global.constants';
import {
  type AuthState,
  type IExchangeTokenResponse,
  type IToken,
  type IUser,
  type LoginRequest,
  type LoginResponse,
  type TokenPayload,
} from '../models/user.interface';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private logger = new Logger('AuthService');

  // 1. Base State
  private _state = signal<AuthState>({
    user: this.loadUserFromStorage(),
    token: this.loadTokenFromStorage(),
    status: 'idle',
    error: null,
  });

  // 2. Readonly signals for components
  readonly user = computed(() => this._state().user);
  readonly token = computed(() => this._state().token);
  readonly status = computed(() => this._state().status);
  readonly isAuthenticated = computed(() => !!this._state().token);
  readonly isAuthenticating = computed(() => this._state().status === 'authenticating');

  // 3. Renewal Management using a Signal trigger + Shared Observable
  private _renewalTrigger = signal<number | null>(null);

  // This shared observable handles concurrent renewal requests
  private _renewal$ = toObservable(this._renewalTrigger).pipe(
    filter((trigger): trigger is number => trigger !== null),
    switchMap(() =>
      this.http
        .get<IExchangeTokenResponse>(`${environment.authApi}/${AUTH_ENDPOINTS.renew_token}`, {
          withCredentials: true,
        })
        .pipe(
          map((res) => ({
            idToken: res.idToken,
            expiresIn: +res.expiresIn,
          })),
          tap((token) => {
            this.persistTokenAndSyncState(token);
            this._renewalTrigger.set(null); // Reset trigger after success
          }),
          catchError((err) => {
            this._renewalTrigger.set(null); // Reset trigger on error
            this.logout();
            return throwError(() => err);
          }),
          shareReplay(1)
        )
    )
  );

  // Expose the renewal state as a signal if needed
  readonly renewalResult = toSignal(this._renewal$);

  constructor() {
    this.checkTokenOnBoot();
  }

  login(loginRequest: LoginRequest) {
    this.logger.log(`Attempting login for user: ${loginRequest.email}`);
    this._state.update((s) => ({ ...s, status: 'authenticating', error: null }));

    return this.http
      .post<LoginResponse>(`${environment.authApi}/${AUTH_ENDPOINTS.login}`, loginRequest, {
        withCredentials: true,
      })
      .pipe(
        map((res) => {
          const { email, displayName, idToken, expiresIn, localId, kind } = res;
          const decoded = jwtDecode<TokenPayload>(idToken);

          const user: IUser = {
            displayName,
            email,
            localId,
            kind,
            tenant: decoded.firebase?.tenant,
            roles: decoded.roles || [],
          };

          const token: IToken = {
            idToken,
            expiresIn: +expiresIn,
          };

          return { user, token };
        }),
        tap(({ user, token }) => {
          this.logger.log(`Login successful for user: ${user.email}`);
          this.persistAuthData(user, token);
          this._state.update((s) => ({
            ...s,
            user,
            token,
            status: 'authenticated',
            error: null,
          }));
        }),
        catchError((err) => {
          this.logger.error('Login failed', err);
          this._state.update((s) => ({ ...s, status: 'error', error: 'Login failed' }));
          return throwError(() => err);
        })
      );
  }

  logout() {
    this.logger.log('Logging out user');
    this.clearAuthData();
    void this.router.navigate([`/${ROUTE_PATH.login}`]);
    return of(null);
  }

  renewToken(): Observable<IToken> {
    this.logger.log('Token renewal requested');
    // If not already renewing, trigger it
    if (this._renewalTrigger() === null) {
      this._renewalTrigger.set(Date.now());
    }

    return this._renewal$.pipe(
      filter((t): t is IToken => !!t),
      take(1),
      shareReplay(1)
    );
  }

  private checkTokenOnBoot() {
    const expiredTime = this.storageService.getLocalStorage<number>(GLOBAL_CONSTANTS.expiredTime);
    const token = this._state().token;

    if (!expiredTime || !token) {
      this._state.update((s) => ({ ...s, status: 'unauthenticated' }));
      return;
    }

    const currentTime = new Date().getTime();
    if (expiredTime <= currentTime + 5 * 60 * 1000) {
      this.renewToken().subscribe();
    } else {
      this._state.update((s) => ({ ...s, status: 'authenticated' }));
    }
  }

  private persistAuthData(user: IUser, token: IToken) {
    const expiredTime = new Date().getTime() + token.expiresIn * 1000;

    this.storageService.setCookie(GLOBAL_CONSTANTS.idToken, token.idToken);
    this.storageService.setCookie(GLOBAL_CONSTANTS.userCookieName, JSON.stringify(user));
    this.storageService.setLocalStorage(GLOBAL_CONSTANTS.expiredTime, expiredTime);
  }

  private persistTokenAndSyncState(token: IToken) {
    const expiredTime = new Date().getTime() + token.expiresIn * 1000;
    this.storageService.setCookie(GLOBAL_CONSTANTS.idToken, token.idToken);
    this.storageService.setLocalStorage(GLOBAL_CONSTANTS.expiredTime, expiredTime);

    this._state.update((s) => ({ ...s, token, status: 'authenticated' }));
  }

  private clearAuthData() {
    this.storageService.removeCookie(GLOBAL_CONSTANTS.idToken);
    this.storageService.removeCookie(GLOBAL_CONSTANTS.userCookieName);
    this.storageService.removeLocalStorage(GLOBAL_CONSTANTS.expiredTime);

    this._state.set({
      user: null,
      token: null,
      status: 'unauthenticated',
      error: null,
    });
    this._renewalTrigger.set(null);
  }

  private loadUserFromStorage(): IUser | null {
    try {
      const userJson = this.storageService.getCookieByName(GLOBAL_CONSTANTS.userCookieName);
      return userJson ? JSON.parse(userJson) : null;
    } catch {
      return null;
    }
  }

  private loadTokenFromStorage(): IToken | null {
    const idToken = this.storageService.getCookieByName(GLOBAL_CONSTANTS.idToken);
    const expiredTime = this.storageService.getLocalStorage<number>(GLOBAL_CONSTANTS.expiredTime);

    if (idToken && expiredTime) {
      const remainingTime = (expiredTime - new Date().getTime()) / 1000;
      if (remainingTime > 0) {
        return {
          idToken,
          expiresIn: remainingTime,
        };
      }
    }
    return null;
  }
}
